import { useCallback, useState } from 'react'
import type { AnalysisMode, Capabilities } from '../../../types'
import { useCapabilities } from './useCapabilities'

/** Volba se pamatuje napříč návštěvami - kdo účtenky dává asistentovi, dělá to pravidelně. */
const KLIC = 'splitup:zpracovani-uctenek'

const ULOZENE: Record<string, AnalysisMode> = { server: 'SERVER', mcp: 'MCP', both: 'BOTH' }

function nactiVolbu(): AnalysisMode | undefined {
    try {
        return ULOZENE[localStorage.getItem(KLIC) ?? '']
    } catch {
        return undefined
    }
}

function ulozVolbu(rezim: AnalysisMode): void {
    try {
        localStorage.setItem(KLIC, rezim.toLowerCase())
    } catch {
        // Zakázané úložiště znamená jen to, že se volba nezapamatuje.
    }
}

/** Kdo účtenky může rozpoznat - podle toho, co má instalace zapnuté. */
export function dostupneRezimy(capabilities: Capabilities | undefined): AnalysisMode[] {
    const server = capabilities?.receiptAnalysis ?? false
    const mcp = capabilities?.receiptMcp ?? false
    // „Server" je k dispozici vždy: bez rozpoznávání znamená ruční doplnění.
    return ['SERVER', ...(mcp ? (['MCP'] as const) : []), ...(server && mcp ? (['BOTH'] as const) : [])]
}

/** Název nástroje, kterým účtenky rozpoznává server. */
export function nazevRozpoznavani(provider: string | null | undefined): string {
    switch (provider) {
        case 'ai':
            return 'Claude'
        case 'local':
            return 'lokální model'
        case 'tesseract':
            return 'OCR'
        default:
            return 'aplikace'
    }
}

export type ZpracovaniUctenek = {
    /** Umí instalace předávat účtenky AI asistentovi přes MCP? */
    mcpDostupne: boolean
    /** Režimy, ze kterých jde vybírat. */
    rezimy: AnalysisMode[]
    /** Hodnota pole `analysisMode` při nahrání účtenek. */
    analysisMode: AnalysisMode
    setAnalysisMode: (rezim: AnalysisMode) => void
}

/**
 * Kdo rozpozná nově nahrané účtenky - server, AI asistent přes MCP, nebo
 * oba, aby šly výsledky porovnat.
 *
 * Bez uložené volby se účtenky asistentovi předávají jen tehdy, když server
 * sám rozpoznávat neumí; jinak by skončily bez návrhu položek.
 */
export function useZpracovaniUctenek(): ZpracovaniUctenek {
    const { data: capabilities } = useCapabilities()
    const [ulozena, setUlozena] = useState(nactiVolbu)

    const rezimy = dostupneRezimy(capabilities)
    const vychozi: AnalysisMode = rezimy.includes('MCP') && !capabilities?.receiptAnalysis ? 'MCP' : 'SERVER'
    const analysisMode = ulozena && rezimy.includes(ulozena) ? ulozena : vychozi

    const setAnalysisMode = useCallback((rezim: AnalysisMode) => {
        ulozVolbu(rezim)
        setUlozena(rezim)
    }, [])

    return { mcpDostupne: capabilities?.receiptMcp ?? false, rezimy, analysisMode, setAnalysisMode }
}
